import jsbridge from '@xpeng/js-bridge'
import XPReport from '@xpeng/gio-report'
import dayjs from 'dayjs'

import { MessageBox } from '../components/message-box'
import { compare, exec, REGEX } from './version'
import { IVehicle } from '../pages/homepage/type'
import { ErrCodes } from './constant'
import { type AxiosError } from '../../types/axios'

import pkg from '../../package.json'

export type Action = 'PUSH' | 'POP' | 'REPLACE'
export type UnregisterCallback = () => void

interface Location<S = unknown> {
  pathname: string;
  search: string;
  state: S;
  hash: string;
  key?: string | undefined;
}

type LocationDescriptor<S = unknown> = string | Location<S>

type LocationListener<S = unknown> = (location: Location<S>, action: Action) => void

type TransitionPromptHook<S = unknown> = (
  location: Location<S>,
  action: Action,
) => string | false | void

interface History<HistoryLocationState = unknown> {
  length: number;
  action: Action;
  location: Location<HistoryLocationState>;
  push(
    location: string | LocationDescriptor<HistoryLocationState>, state?: HistoryLocationState
  ): void;
  replace(
    location: string | LocationDescriptor<HistoryLocationState>, state?: HistoryLocationState
  ): void;
  go(n: number): void;
  goBack(): void;
  goForward(): void;
  block(prompt?: boolean | string | TransitionPromptHook<HistoryLocationState>): UnregisterCallback;
  listen(listener: LocationListener<HistoryLocationState>): UnregisterCallback;
  createHref(location: Location<HistoryLocationState>): string;
}

/**
 * 获取用户定位
 */
export function getUserCurrentPosition() {
  // if (isXpAPP()) {
  //   return getCurrentLocationFromApp()
  // }
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(positions => {
        if (positions) {
          resolve(positions)
        } else {
          reject(new Error('获取定位权限失败'))
        }
      }, err => {
        console.warn(`ERROR(${err.code}): ${err.message}`)
        reject(err)
      })
    } else {
      reject(new Error('暂不支持获取定位功能'))
    }
  })
}
/**
 * 像素转换
 */
export function sassPx2Rem(px) {
  const calcPX = Math.abs(px)
  if (px === 0) return '0'
  return px < 0 ? `-${(calcPX * 10) / 750}rem` : `${(calcPX * 10) / 750}rem`
}

export function getDayOfWeek(date) {
  const day = new Date(date).getDay()
  const weekArr = [
    '星期日',
    '星期一',
    '星期二',
    '星期三',
    '星期四',
    '星期五',
    '星期六'
  ]
  return weekArr[day]
}

export function formatVehiclePlate(plate: string, series?: string): string {
  // return `${plate.slice(0, 2)}·${plate.slice(2)}${series ? ` (${series})` : ''}`
  return `${plate.slice(0, 2)} ${plate.slice(2)}${series ? ` (${series})` : ''}`
}

export function desensitizedVin(vin: string): string {
  return (vin && vin.length === 17) ?
    `${vin.slice(0, 3)}***${vin.slice(-3)}` :
    '--'
}

export function isXpAPP() {
  const UA = window.navigator.userAgent

  return (
    UA.indexOf('XiaoPengQiChe') !== -1 ||
    UA.indexOf('xiaopeng') !== -1 ||
    UA.indexOf('com.tentcoo.XiaoPengQiCheEnterprise') !== -1
  )
}

export function isSpecialAPP() {
  const ua = navigator.userAgent.toLowerCase()

  return (
    Boolean(ua.match(/MicroMessenger/gi)) || // 微信内置浏览器
    Boolean(ua.match(/dingtalk/gi)) || // 钉钉内置浏览器
    Boolean(ua.match(/weibo/gi)) || // 微博内置浏览器
    Boolean(ua.match(/feishu/gi)) || // 飞书内置浏览器
    Boolean(ua.match(/AlipayClient/gi)) || // 支付宝浏览器
    (ua.match(/\sqq/gi) && ua.indexOf('mqqbrowser') < 0) // QQ 内置浏览器
  )
}

/**
 * 返回当前页面的环境，依赖于当前域名
 */
export function getClientEnv() {
  return process.env.REACT_APP_DEPLOY_ENV || 'dev'
}

interface ReportInstance {
  send: (data: Record<string, string | number | boolean>) => void
}

let reportInstance: ReportInstance | null = null

/**
 * 埋点上报
 * @param params 业务自定义参数，必须要mcode（日志类型代码），其余参数可选，例如：ch（渠道）、ut（用户类型： 1-潜客， 2-预定车主， 3-车主）
 * @param type 类型： 页面曝光-page， 页面元素曝光-element， 事件-event
 */
export function reportUserBehavior(params: Record<string, string | number | boolean | undefined | null>, type = 'event') {
  if (!reportInstance) {
    const env = getClientEnv()
    const options = env === 'production' ? {
      accountId: '84eafaa69d23412f',
      dataSourceId: 'b1a59794dfbcd715',
      host: 'collect.xiaopeng.com'
    } : {
      accountId: '89a922449121f7c2',
      dataSourceId: 'a74e33acd32e0275',
      host: 'collect.test.xiaopeng.com'
    }
    Object.assign(options, {
      env,
      compress: true,
      version: pkg?.version,
      debug: !env || ['dev'].includes(env)
    })
    reportInstance = new XPReport({
      appid: 'J001',
      so: 'J001002'
    }, options)
  }
  if (!reportInstance) {
    return
  }
  const filteredParams: Record<string, string | number | boolean> = {}
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null) {
      filteredParams[key] = value
    }
  })
  const { mcode, ...restParams } = filteredParams
  if (!restParams.st) {
    restParams.st = dayjs().format('YYYY-MM-DD HH:mm:ss')
  }
  reportInstance.send({
    eventId: 'Web_Insurance_Event',
    mcode: mcode || window.location.href,
    type,
    ...restParams
  })
}

/**
 * 获取平台
 */
export function getPlatform() {
  const UA = navigator.userAgent || ''
  const isHarmony = /HarmonyOS|OpenHarmony|ArkWeb/i.test(UA)
  if (isHarmony) {
    return 'Harmony'
  }
  const isAndroid = UA.indexOf('Android') > -1 || UA.indexOf('Adr') > -1
  if (isAndroid) {
    return 'Android'
  }
  const isIOS = !!UA.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/)
  return isIOS ? 'iOS' : 'PC'
}

export function formatTime(time, format) {
  const date = new Date(Number(time))
  const obj = {
    'M+': date.getMonth() + 1,
    'D+': date.getDate(),
    'H+': date.getHours(),
    'm+': date.getMinutes(),
    's+': date.getSeconds()
  }
  if (/(Y+)/.test(format)) {
    format = format.replace(
      RegExp.$1,
      date
        .getFullYear()
        .toString()
        .substr(4 - RegExp.$1.length)
    )
  }
  Object.keys(obj).forEach(key => {
    if (new RegExp(`(${key})`).test(format)) {
      const value =
        RegExp.$1.length === 1 ?
          obj[key] :
          `00${obj[key]}`.substr(obj[key].toString().length)
      format = format.replace(RegExp.$1, value)
    }
  })
  return format
}

/**
 * 校验版本信息
 */
export function checkVersion(version: string, operator = '<=') {
  const str = window.navigator.userAgent.toLowerCase()
  const results = exec(REGEX, str)
  const result = results ? results[1] : null
  if (!result) {
    return false
  }
  const finalResult = result.trim()
  return compare(version, finalResult, operator)
}

/**
 * @description: 格式化金额
 * @param {number} number 要格式化的数字
 * @param {number} decimal 保留几位小数
 * @param {string} decPoint 小数点符号
 * @param {string} thousandsSep 千分位符号
 * @param {string} roundTag 舍入参数，默认 "ceil" 向上取,"floor"向下取,"round" 四舍五入
 * @return: 格式化后的金额
 */
export function formatNumber(
  number: number,
  decimal = 2,
  decPoint = '.',
  thousandsSep?: string,
  roundTag: 'ceil' | 'floor' | 'round' = 'round'
): string {
  const _formatNumber = (`${number}`).replace(/[^0-9+-Ee.]/g, '')
  roundTag = roundTag || 'round' // "ceil","floor","round"
  const n = !Number.isFinite(+_formatNumber) ? 0 : +_formatNumber
  const prec = !Number.isFinite(+decimal) ? 0 : Math.abs(decimal)
  const sep = thousandsSep || ''
  const dec = decPoint
  const s = ''
  const toFixedFix = (_n: number, _prec: number): string => {
    const k = 10 ** _prec

    return (
      `${parseFloat(
        Math[roundTag](parseFloat((_n * k).toFixed(_prec * 2))).toFixed(
          _prec * 2
        )
      ) /
      k}`
    )
  }
  const ss: string[] = (prec ? toFixedFix(n, prec) : `${Math.round(n)}`).split(
    '.'
  )
  const re = /(-?\d+)(\d{3})/
  while (re.test(s[0])) {
    ss[0] = ss[0].replace(re, `$1${sep}$2`)
  }

  if ((ss[1] || '').length < prec) {
    ss[1] = ss[1] || ''
    ss[1] += new Array(prec - ss[1].length + 1).join('0')
  }
  return ss.join(dec)
}

export function isObject(value) {
  const type = typeof value
  return value != null && (type === 'object' || type === 'function')
}

export function debounce(func, wait, options) {
  let lastArgs
  let lastThis: any
  let maxWait
  let result
  let timerId
  let lastCallTime

  let lastInvokeTime = 0
  let leading = false
  let maxing = false
  let trailing = true

  // Bypass `requestAnimationFrame` by explicitly setting `wait=0`.
  const useRAF = (!wait && wait !== 0 && typeof window.requestAnimationFrame === 'function')

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function')
  }
  wait = +wait || 0
  if (isObject(options)) {
    leading = !!options.leading
    maxing = 'maxWait' in options
    maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : maxWait
    trailing = 'trailing' in options ? !!options.trailing : trailing
  }

  function invokeFunc(time) {
    const args = lastArgs
    const thisArg = lastThis

    lastArgs = lastThis = undefined
    lastInvokeTime = time
    result = func.apply(thisArg, args)
    return result
  }

  function startTimer(pendingFunc: () => void, _wait) {
    if (useRAF) {
      window.cancelAnimationFrame(timerId)
      return window.requestAnimationFrame(pendingFunc)
    }
    return setTimeout(pendingFunc, _wait)
  }

  function cancelTimer(id) {
    if (useRAF) {
      window.cancelAnimationFrame(id)
    }
    clearTimeout(id)
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime
    const timeSinceLastInvoke = time - lastInvokeTime
    const timeWaiting = wait - timeSinceLastCall

    return maxing ?
      Math.min(timeWaiting, maxWait - timeSinceLastInvoke) :
      timeWaiting
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime
    const timeSinceLastInvoke = time - lastInvokeTime

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait))
  }

  function trailingEdge(time) {
    timerId = undefined

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time)
    }
    lastArgs = lastThis = undefined
    return result
  }

  function timerExpired() {
    const time = Date.now()
    if (shouldInvoke(time)) {
      return trailingEdge(time)
    }
    // Restart the timer.
    timerId = startTimer(timerExpired, remainingWait(time))
    return timerId
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time
    // Start the timer for the trailing edge.
    timerId = startTimer(timerExpired, wait)
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result
  }

  function cancel() {
    if (timerId !== undefined) {
      cancelTimer(timerId)
    }
    lastInvokeTime = 0
    lastArgs = lastCallTime = lastThis = timerId = undefined
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(Date.now())
  }

  function pending() {
    return timerId !== undefined
  }

  function debounced(...args) {
    const time = Date.now()
    const isInvoking = shouldInvoke(time)

    lastArgs = args
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    lastThis = this
    lastCallTime = time

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime)
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        timerId = startTimer(timerExpired, wait)
        return invokeFunc(lastCallTime)
      }
    }
    if (timerId === undefined) {
      timerId = startTimer(timerExpired, wait)
    }
    return result
  }
  debounced.cancel = cancel
  debounced.flush = flush
  debounced.pending = pending
  return debounced
}

export function throttle(func, wait?: number, options?: any) {
  let leading = true
  let trailing = true

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function')
  }
  if (isObject(options)) {
    leading = 'leading' in options ? !!options.leading : leading
    trailing = 'trailing' in options ? !!options.trailing : trailing
  }
  return debounce(func, wait, {
    leading,
    trailing,
    maxWait: wait
  })
}

export function getPopupLabelOfVehicle(item: IVehicle): string {
  const { plateNumber, vin, modelName, modelSeries } = item
  if (plateNumber) {
    return formatVehiclePlate(plateNumber, modelSeries)
  }

  const desVin = desensitizedVin(vin)

  if (modelName) {
    return `${modelName}（${desVin}）`
  }

  return desVin
}

export function isNil(arg: unknown): arg is undefined | null {
  return arg === undefined || arg === null
}

export function isEmpty(arg: unknown) {
  if (arg === undefined || arg === null) {
    return true
  }
  if (Array.isArray(arg)) {
    return arg.length === 0
  }
  if (typeof arg === 'string') {
    return arg.length === 0
  }
  if (arg instanceof Set) {
    return arg.size === 0
  }
  if (arg instanceof Map) {
    return arg.size === 0
  }
  if (typeof arg === 'object') {
    return Object.keys(arg).length === 0
  }
  return false
}

export function getRequestErrMsg(
  e?: string | Error | AxiosError | unknown, defaultErrMsg?: string
): string {
  let errMsg: string | undefined

  if (!e) {
    errMsg = undefined
  } else if (typeof e === 'string') {
    errMsg = e
  } else if (Reflect.has(e, 'isAxiosError')) {
    const axiosError: AxiosError = e as AxiosError
    const { data, status, statusText } = axiosError.response || {}
    const { msg } = data || {}
    if (msg) {
      errMsg = msg
    } else if (axiosError.code === 'ECONNABORTED') {
      errMsg = '请求超时，请稍后重试'
    } else if (axiosError.code === 'ERR_NETWORK') {
      errMsg = '网络异常，请稍后重试'
    } else {
      errMsg = statusText || axiosError.message || (isNil(status) ? defaultErrMsg : `请求失败，状态码：${status}`)
    }
  } else if (Reflect.has(e, 'message')) {
    const normalError: Error = e as Error
    const { message } = normalError
    errMsg = message
  }

  if (!errMsg) {
    return defaultErrMsg || '请求失败'
  }

  if (errMsg.includes('timeout')) {
    return '请求超时，请稍后重试'
  }

  if (errMsg.includes('Network Error')) {
    return '网络异常，请稍后重试'
  }
  return errMsg
}

export function toRem(pxNumber: number) {
  if (Math.abs(pxNumber) > 1) {
    return `${(pxNumber / 100)}rem`
  }
  return `${pxNumber}px`
}

/*
 * 安全调用APP的方法
 * @param fn 方法名
 * @param params 方法入参
 * @param wrapper 是否包裹一层
 * @returns
 */
export function safetyCallApp(
  fn: string,
  params?: Record<string, string | number | boolean>,
  wrapper = false
) {
  return new Promise((resolve, reject) => {
    const isInApp = isXpAPP()
    if (!isInApp && wrapper) {
      resolve({ errCode: ErrCodes.NOT_XP_APP, errMsg: '请打开小鹏APP' })
      return
    }

    if (!isInApp) {
      reject(new Error('请打开小鹏APP'))
      return
    }

    const callHandler = jsbridge?.callHandler
    if (typeof callHandler === 'function') {
      callHandler(fn, params).then(res => {
        wrapper && resolve({ errCode: ErrCodes.SUCCESS, result: res })
        !wrapper && resolve(res)
      }).catch(e => {
        reject(e)
      })
      return
    }

    if (wrapper) {
      resolve({ errCode: ErrCodes.JS_ERROR, errMsg: 'jsbridge加载异常' })
    } else {
      reject(new Error('jsbridge加载异常'))
    }
  })
}

/**
 * generate key for element
 * @param args
 * @returns string | undefined
 */
export function generateKey(...args: (string | number | undefined | null | Record<string, any>)[]) {
  if (!args?.length) {
    return undefined
  }
  const filteredArgs: string[] = []
  args.forEach(arg => {
    if (arg === undefined || arg === null) {
      return
    }
    if (typeof arg === 'string') {
      filteredArgs.push(arg)
      return
    }
    if (typeof arg === 'number') {
      filteredArgs.push(`${arg}`)
      return
    }
    if (typeof arg === 'boolean') {
      filteredArgs.push(`${arg}`)
      return
    }
    if (Array.isArray(arg)) {
      const itemResult = generateKey(arg)
      itemResult && filteredArgs.push(itemResult)
      return
    }
    if (typeof arg === 'object') {
      Object.keys(arg).forEach(key => {
        const itemValue = arg[key]
        const itemResult = generateKey(itemValue)
        itemResult && filteredArgs.push(`${key}=${itemResult}`)
      })
    }
  })
  if (!filteredArgs?.length) {
    return undefined
  }
  return filteredArgs.join('_')
}

/**
 * append params query string to url
 * @param url
 * @param params
 * @param strict if strict is true, will filter empty string value
 * @returns string
 */
export function appendUrlParams(url: string | undefined | null, params: Record<string, string | number | undefined | null>, strict = false) {
  if (!url) {
    return ''
  }
  const filteredParams: Record<string, string> = {}
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value === undefined || value === null) {
      return
    }
    if (strict && value === '') {
      return
    }
    filteredParams[key] = `${value}`
  })
  if (!Object.keys(filteredParams).length) {
    return url
  }
  const appendString = new URLSearchParams(filteredParams).toString()
  const index = url.lastIndexOf('?')

  if (index < 0) {
    return `${url}?${appendString}`
  }
  if (index === url.length - 1) {
    return `${url}${appendString}`
  }
  return `${url}&${appendString}`
}

/**
 * 通用跳转逻辑
 * @param history
 * @param url
 * @param data
 * @param isReplace
 */
export function handleJump(
  history: History, url?: string, data?: Record<string, any> | null, isReplace = false
) {
  if (!url) {
    MessageBox.toast('链接为空无法打开！')
    return
  }

  let finalUrl: string
  let isInnerRoute = false
  const protocols = ['https://', 'http://']
  const matchProtocol = protocols.find(protocol => url.startsWith(protocol))
  if (matchProtocol) {
    // absolutely url
    const urlWithoutProtocol = url.replace(matchProtocol, '')
    isInnerRoute = urlWithoutProtocol.startsWith(window.location.host)
    finalUrl = isInnerRoute ? urlWithoutProtocol.replace(window.location.host, '') : url
  } else if (url.startsWith('/')) {
    // insurance h5 route
    isInnerRoute = true
    finalUrl = url
  } else if (isXpAPP()) {
    // xp app deep link
    jsbridge.callHandler('openUrl', {
      url,
      data
    })
    return
  } else {
    // xp app deep link
    MessageBox.toast('请在小鹏app内打开')
    return
  }

  if (!isInnerRoute && isReplace) {
    // 外链
    window.location.replace(finalUrl)
    return
  }
  if (!isInnerRoute) {
    // 外链
    window.location.href = finalUrl
    return
  }
  finalUrl = finalUrl.startsWith('/insurance/') ? finalUrl.replace('/insurance/', '/') : finalUrl
  finalUrl = finalUrl.replace(/\/\//g, '/')

  if (!isNil(data) && !isEmpty(data)) {
    const queryArray: string[] = []
    Object.keys(data).forEach(key => {
      queryArray.push(`${key}=${typeof data[key] === 'string' ? data[key] : JSON.stringify(data[key])}`)
    })
    const query = queryArray.join('&')
    const index = finalUrl.lastIndexOf('?')
    finalUrl = index < 0 ? `${finalUrl}?${query}` : `${finalUrl}&${query}`.replace('?&', '?')
  }
  if (isReplace) {
    history.replace(finalUrl)
  } else {
    history.push(finalUrl)
  }
}

export function stringifyVehicle(
  vehicle: IVehicle | undefined,
  key = 'sv',
  defaultValue: string | undefined | null = '{}',
  spearator: string | undefined | null = '&'
) {
  const finalSpearator = spearator || ''
  if (!vehicle) {
    return defaultValue ? `${finalSpearator}${key}=${defaultValue}` : ''
  }

  const filteredVehicle: Partial<IVehicle> = {}
  Object.keys(vehicle).forEach(vehicleKey => {
    const value = vehicle[vehicleKey]
    if (isNil(value)) {
      return
    }

    if (value === '') {
      return
    }
    filteredVehicle[vehicleKey] = value
  })
  return `${finalSpearator}${key}=${JSON.stringify(filteredVehicle)}`
}

// 给字符串里所有的手机号脱密, 例如: phone: 12345678901 => phone: 123****8901
export const desensitizePhoneNumbers = (text: string) => {
  if (!text) {
    return text
  }
  // 使用精确的正则表达式以确保只匹配独立的11位手机号码
  const phoneRegex = /\b1[0-9]\d{9}\b/g
  // 替换手机号码中间四位数字为星号
  return text.replace(phoneRegex, match => `${match.substring(0, 3)}****${match.substring(7)}`)
}

// 给字符串里所有的身份证号脱密, 例如: id: 123456789012345678 => id: 123456********5678
export const desensitizeIdNumbers = (text: string) => {
  if (!text) {
    return text
  }
  // 匹配18位身份证号码的正则表达式
  const idRegex = /\b\d{6}\d{8}\d{3}[\dXx]\b/g
  // 使用String.prototype.replace方法和一个回调函数来进行脱敏处理
  // 保留前六位和后四位，中间用星号替换
  return text.replace(idRegex, match => `${match.substring(0, 6)}********${match.substring(14)}`)
}

export const getDesensitizedResult = params => {
  if (!params) {
    return params
  }
  if (typeof params === 'string') {
    return desensitizeIdNumbers(desensitizePhoneNumbers(params))
  }
  try {
    const desensitizedParams = desensitizePhoneNumbers(desensitizeIdNumbers(JSON.stringify(params)))
    return JSON.parse(desensitizedParams)
  } catch (e) {
    console.log(e)
    return params
  }
}
